import React, { useState } from 'react'
import { Button, Checkbox, Dropdown, Input } from 'semantic-ui-react'
import { useRouter } from 'next/router'

import { useAppSelector } from 'redux/hooks'

import { CONDITION_DROPDOWN, LANGUAGE_DROPDOWN } from 'types/collection'
import { Finishes } from 'types/deck'

import CollectionService from 'services/collection.service'
import ToastService from 'services/toast.service'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import LabelDisplay from 'components/collectionV2/misc/LabelDisplay'
import ProgressLoadingBar from 'components/elements/ProgressLoadingBar'

import { stringListToDropdown } from 'utils/DropdownConversions'

import styles from './multiEditModal.module.scss'

type Props = {
  open: boolean
  onClose: () => void
}

const TAG_OPTION_OPTIONS = [
  { text: 'Add to existing', value: 'add' },
  { text: 'Clear and replace', value: 'replace' },
]

const MultiEditModal: React.FC<Props> = ({ open, onClose }: Props) => {
  const router = useRouter()

  const tags = useAppSelector(state => state.collectionV2.tags)
  const multiSelectedIds = useAppSelector(state => state.collectionV2.multiSelectedIds)

  const [loading, setLoading] = useState(false)
  const [completed, setCompleted] = useState(0)
  const [tagOption, setTagOption] = useState<'add' | 'replace'>('add')

  const [formValues, setFormValues] = useState({
    quantity: 1,
    modifier: 'Normal' as Finishes,
    language: 1,
    condition: 1,
    tags: [] as number[],
  })

  const [selectedFields, setSelectedFields] = useState({
    quantity: false,
    modifier: false,
    language: false,
    condition: false,
    tags: false,
  })

  const handleSubmit = async () => {
    const updates: any = {}

    if (selectedFields.quantity) updates.quantity = formValues.quantity
    if (selectedFields.modifier) updates.modifier = formValues.modifier
    if (selectedFields.language) updates.language = formValues.language
    if (selectedFields.condition) updates.condition = formValues.condition
    if (selectedFields.tags) updates.tags = formValues.tags

    let completed = 0 // For progress bar - updating it directly off the state variable isn't happy in the loop

    const allIds = Object.keys(multiSelectedIds)
    const MAX_UPDATE_SIZE = 25

    setLoading(true)

    try {
      for (let i = 0; i < allIds.length; i += MAX_UPDATE_SIZE) {
        const ids = allIds.slice(i, i + MAX_UPDATE_SIZE)

        await CollectionService.modifyCards(updates, ids, tagOption)

        completed += ids.length

        setCompleted(completed)
      }
    } catch (error) {
      ToastService.create('Failed to update current batch of cards', 'Collection page')

      setLoading(false)
    }

    router.reload()
  }

  const totalSelected = Object.keys(multiSelectedIds).length
  const hasEdits = !Object.values(selectedFields).some(value => value)

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <div className={styles.content}>
        <h2>Edit {totalSelected} card(s)</h2>
        <div>
          <div className={styles.fieldGroup}>
            <div className={styles.sectionHeader}>
              <Checkbox
                label="Quantity"
                checked={selectedFields.quantity}
                onChange={() => setSelectedFields({ ...selectedFields, quantity: !selectedFields.quantity })}
              />
            </div>
            <Input
              disabled={!selectedFields.quantity}
              value={formValues.quantity}
              onChange={(_e, { value }) => setFormValues({ ...formValues, quantity: Number(value.replace(/\D/g, '')) })}
            />
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.sectionHeader}>
              <Checkbox
                label="Foiling"
                checked={selectedFields.modifier}
                onChange={() => setSelectedFields({ ...selectedFields, modifier: !selectedFields.modifier })}
              />
            </div>
            <Dropdown
              selection
              disabled={!selectedFields.modifier}
              value={formValues.modifier}
              options={stringListToDropdown(['Normal', 'Foil', 'Etched'])}
              onChange={(_e, { value }) => setFormValues({ ...formValues, modifier: value as Finishes })}
            />
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.sectionHeader}>
              <Checkbox
                label="Language"
                checked={selectedFields.language}
                onChange={() => setSelectedFields({ ...selectedFields, language: !selectedFields.language })}
              />
            </div>
            <Dropdown
              selection
              disabled={!selectedFields.language}
              value={formValues.language}
              options={LANGUAGE_DROPDOWN}
              onChange={(_e, { value }) => setFormValues({ ...formValues, language: value as number })}
            />
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.sectionHeader}>
              <Checkbox
                label="Condition"
                checked={selectedFields.condition}
                onChange={() => setSelectedFields({ ...selectedFields, condition: !selectedFields.condition })}
              />
            </div>
            <Dropdown
              selection
              disabled={!selectedFields.condition}
              value={formValues.condition}
              options={CONDITION_DROPDOWN}
              onChange={(_e, { value }) => setFormValues({ ...formValues, condition: value as number })}
            />
          </div>

          <div className={styles.fieldGroup}>
            <div className={styles.sectionHeader}>
              <Checkbox
                label="Collection labels"
                checked={selectedFields.tags}
                onChange={() => setSelectedFields({ ...selectedFields, tags: !selectedFields.tags })}
              />
            </div>
            <Dropdown
              selection
              multiple
              search
              placeholder="Used, on loan, etc"
              disabled={!selectedFields.tags}
              value={formValues.tags}
              options={Object.values(tags).map(tag => ({
                key: tag.id,
                value: tag.id,
                text: tag.name,
                content: <LabelDisplay name={tag.name} color={tag.color} />,
              }))}
              onChange={(_e, { value }) => setFormValues({ ...formValues, tags: value as number[] })}
            />
            <Dropdown
              style={{ marginLeft: 10 }}
              placeholder="No tags"
              disabled={!selectedFields.tags}
              value={tagOption}
              options={TAG_OPTION_OPTIONS}
              onChange={(_e, { value }) => setTagOption(value as 'add' | 'replace')}
            />
          </div>
        </div>
        <div className={styles.controls}>
          <Button onClick={handleSubmit} color="green" disabled={hasEdits || loading}>
            Mass edit {totalSelected} card(s)
          </Button>
        </div>

        {loading && (
          <ProgressLoadingBar
            label="Batch updating collection cards..."
            completionPercentage={completed / totalSelected}
          />
        )}
      </div>
    </GlobalOverlayStack>
  )
}

export default MultiEditModal
